import React, { useMemo } from 'react';
import {
  Banner,
  Header,
  Highlight,
  Layout,
  SideBar,
  Viewer,
  UserInterfaceControls,
} from '../components';
import { Leva, useControls } from 'leva';
import { useHighlightEngine } from '../components/Highlight';
import { useParams } from 'react-router';
import { useGetScoreCardQuery, useGetTournamentQuery } from '@slope/graphql/apollo';
import { shortenName } from '@slope/utils';
import { Player } from '@slope/graphql/resolvers';
import { useStore } from '@slope/store';

const shortPlayerNames = (names: Pick<Player, 'firstName' | 'lastName'>[]) => {
  if (names.length === 0) return '';
  if (names.length === 1) return `${names[0].firstName} ${names[0].lastName}`;

  return names.map(({ firstName, lastName }) => shortenName(`${firstName} ${lastName}`)).join(', ');
};

export const RoundView: React.FC = () => {
  const tournamentId = useParams<{ tournamentId: string; roundNumber: string }>().tournamentId!;
  const roundNumber = useParams<{ tournamentId: string; roundNumber: string }>().roundNumber!;

  // Fetch tournament data to get the host club slug
  const { data: tournamentData } = useGetTournamentQuery({
    variables: { id: tournamentId },
  });

  useHighlightEngine({
    pause: roundNumber !== 'live',
    tournamentId,
    minIdleGapMs: 5_000,
    maxConsecutive: 5,
    repeatIntervalMs: 5_000,
  });

  const highlight = useStore(({ highlight }) => highlight);

  const participationId = highlight?.participation?.id;

  const { data } = useGetScoreCardQuery({
    skip: !participationId,
    variables: {
      participationId: participationId!,
    },
  });

  const scoreStrip = useMemo(
    () =>
      (data?.scoreCard.holes ?? []).map(({ result, par, holeNumber }) => ({
        hole: holeNumber,
        strokes: result.__typename === 'ScoreEntry' ? result.strokes : null,
        par,
      })),
    [data?.scoreCard.holes],
  );

  const scoredHole = data?.scoreCard.holes.find((hole) => hole.holeNumber === highlight?.hole.number)?.result;

  const total = useMemo(() => data?.scoreCard.total, [data?.scoreCard.total]);

  const { selectedHole } = useControls({
    selectedHole: {
      options: [
        'all',
        'hole_1',
        'hole_2',
        'hole_3',
        'hole_4',
        'hole_5',
        'hole_6',
        'hole_7',
        'hole_8',
        'hole_9',
        'hole_10',
        'hole_11',
        'hole_12',
        'hole_13',
        'hole_14',
        'hole_15',
        'hole_16',
        'hole_17',
        'hole_18',
      ] as const,
      value: 'all',
      label: 'Hole',
    },
  });

  const selectedResult: 'toPar' | 'strokes' | 'stableford' = 'toPar' as const;
  console.log(scoredHole);

  return (
    <>
      <Layout
        style={{ position: 'absolute', inset: 0, zIndex: 1 }}
        left={<SideBar />}
        right={
          highlight && (
            <Highlight
              title={highlight.title}
              eventType={'BIRDIE'}
              timestamp={new Date(highlight.createdAt)}
              wind={{ speedMs: 3, dirDeg: 25 }}
              hole={{
                ...highlight.hole,
                pinPosition: '5F 10D',
                historicalAvg: 4.35,
              }}
              player={{
                name: shortPlayerNames(highlight.participation.players),
                rank: '4',
                score: scoredHole?.__typename === 'ScoreEntry' ? scoredHole[selectedResult] : null,
                total: total?.[selectedResult] ?? 0,
              }}
              strip={scoreStrip}
              eagles={['Harrell Sutton', 'Juan Fernández']}
              birdies={['Carlos Domínguez', 'Juan Fernández']}
              pairs={['Player A', 'Player B']}
            />
          )
        }
        leftProps={{ expanded: !highlight, width: '30vw', minWidth: '400px' }}
        rightProps={{ expanded: !!highlight, width: '70vw' }}
        footerProps={{ expanded: !highlight }}
        footer={<Banner />}
        header={<Header />}
      >
        <Viewer
          tournamentId={tournamentId}
          slug={tournamentData?.tournament.host?.slug}
          selectedHole={
            /hole_\d+/.test(selectedHole)
              ? parseInt(selectedHole.split('_')[1])
              : highlight?.hole.number
          }
        />
      </Layout>
      <Leva hidden={new URLSearchParams(document.location.search).has('debug') === false} />
      <UserInterfaceControls />
    </>
  );
};
